import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { filter, forEach } from 'lodash';
import usePrintLabelContext from 'common/hooks/usePrintLabelContext';

// Keyed by both, because a productId order has no NDC until fulfillment. Mirrors the row disable
// rule in BulkLabelsTable.
const lockKeysOf = (lockedItems) => {
  const lockKeys = new Set();

  forEach(lockedItems, ({ ndc, productId, fulfillingPharmacyId }) => {
    if (ndc) lockKeys.add(`${ndc}::${fulfillingPharmacyId}`);
    if (productId) lockKeys.add(`${productId}::${fulfillingPharmacyId}`);
  });

  return lockKeys;
};

const isRowLocked = (lockKeys, { ndc, productId, fulfillingPharmacyId }) =>
  lockKeys.has(`${ndc}::${fulfillingPharmacyId}`) ||
  (!!productId && lockKeys.has(`${productId}::${fulfillingPharmacyId}`));

const nameOf = ({ drugName, strength, ndc }) => {
  const base = drugName || ndc || 'A medication';
  return strength ? `${base} ${strength}` : base;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const subjectOf = (items) => {
  const others = items.length - 1;

  return others > 0
    ? `${nameOf(items[0])} and ${plural(others, 'other medication')} are`
    : `${nameOf(items[0])} is`;
};

const lockedMessage = (items, evictedCount) => {
  const notice = `${subjectOf(items)} now locked for physical count.`;

  return evictedCount > 0
    ? `${notice} ${plural(evictedCount, 'item')} removed from your selection.`
    : notice;
};

const releasedMessage = (items) => `${subjectOf(items)} no longer locked for physical count.`;

/**
 * Announces physical count lock changes and keeps unfillable rows out of the selection (POM-6033).
 *
 * Eviction runs on any change; announcement only on a socket push. A read cannot say WHEN a lock
 * happened, so announcing from one calls an hours-old count "now locked" on every page load.
 */
const usePhysicalCountLockNotice = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { labelItemIds, updateLabelItemIds } = usePrintLabelContext();
  const lockedInventoryItems = useSelector(({ workQueue }) => workQueue.lockedInventoryItems);
  const lastDelta = useSelector(({ workQueue }) => workQueue.lockedInventoryLastDelta);
  const deltaSeq = useSelector(({ workQueue }) => workQueue.lockedInventoryDeltaSeq);
  const { pages, currentPage } = useSelector(({ workQueue }) => workQueue.bulkLabels);

  // Via refs so a checkbox click does not re-run this — only a lock change should.
  const labelItemIdsRef = useRef(labelItemIds);
  labelItemIdsRef.current = labelItemIds;

  const rowsRef = useRef([]);
  rowsRef.current = pages?.[currentPage] ?? [];

  const announcedSeqRef = useRef(0);

  useEffect(() => {
    const selectedIds = labelItemIdsRef.current ?? [];
    const lockKeys = lockKeysOf(lockedInventoryItems);

    const evicted = filter(
      rowsRef.current,
      (row) => selectedIds.includes(row.lineItemId) && isRowLocked(lockKeys, row)
    );

    // Via updateLabelItemIds, not a reset, so the line-item lock is released to other users too.
    forEach(evicted, ({ lineItemId }) =>
      updateLabelItemIds({ isSelected: false, labelItemId: lineItemId })
    );

    // The list moved because of a read, not a push — state, not news.
    if (deltaSeq === announcedSeqRef.current || !lastDelta?.items?.length) return;

    announcedSeqRef.current = deltaSeq;

    if (lastDelta.changeType === 'LOCKED') {
      enqueueSnackbar(lockedMessage(lastDelta.items, evicted.length), {
        // Warning only when something left the user's hands.
        variant: evicted.length > 0 ? 'warning' : 'info',
        preventDuplicate: true,
        autoHideDuration: 8000,
      });
      return;
    }

    if (lastDelta.changeType === 'UNLOCKED') {
      enqueueSnackbar(releasedMessage(lastDelta.items), {
        variant: 'success',
        preventDuplicate: true,
        autoHideDuration: 6000,
      });
    }
  }, [lockedInventoryItems, deltaSeq, lastDelta, updateLabelItemIds, enqueueSnackbar]);
};

export default usePhysicalCountLockNotice;
